import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Pressable, ScrollView } from '@rbilabs/universal-components';
import { TextInput as RNTextInput, StyleSheet } from 'react-native';

import Currency from 'components/currency';
import { TextInput } from 'components/ucl';
import { Box, BoxProps, Typography } from 'components/ui';
import { LogUserErrorSeverity } from 'hooks/log-user-error-message';
import { useLocale } from 'state/intl';
import { getCurrencyPrefix } from 'state/intl/get-currency-prefix';
import { t } from 'state/intl/translations';
import { TipAmounts } from 'state/order/hooks/use-cart-tip';
import { getDesignToken, primitive, spacing } from 'styles/constants';

import { useCheckoutLoadingState } from '../../hooks';
import { useCheckoutValidation } from '../../hooks/use-checkout-validation';
import { BaseCheckoutWidgetProps } from '../../types';

import { useTip } from './use-tip';

const TipList = ScrollView.withConfig({
  keyboardShouldPersistTaps: 'always',
  width: 'full',
  overflow: 'hidden',
  borderLeftRadius: 4,
  borderRightRadius: 4,
  padding: 0,
  marginY: '$2',
});

const PressableTip = Pressable.withConfig<{
  $isSelected: boolean;
  $isFirst?: boolean;
  $isLast?: boolean;
  $isTipLocked?: boolean;
  $isDisabled?: boolean;
}>(p => ({
  borderRightWidth: 1,
  borderLeftWidth: 1,
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderRightColor: primitive.$blackOpacity20,
  borderLeftColor: primitive.$blackOpacity20,
  borderTopColor: primitive.$blackOpacity20,
  borderBottomColor: primitive.$blackOpacity20,
  backgroundColor: p.$isSelected
    ? p.$isDisabled || p.$isTipLocked
      ? primitive.$blackOpacity50
      : primitive.$bbqBrown
    : p.$isDisabled || p.$isTipLocked
    ? primitive.$blackOpacity4
    : primitive.$white,
  padding: 3,
  borderLeftRadius: p.$isFirst ? 4 : 0,
  borderRightRadius: p.$isLast ? 4 : 0,
}));

const TipText = ({
  $isSelected,
  $isTipLocked,
  $isDisabled,
  ...props
}: React.ComponentPropsWithoutRef<typeof Typography> & {
  $isSelected: boolean;
  $isTipLocked?: boolean;
  $isDisabled?: boolean;
}) =>
  React.createElement(Typography, {
    textAlign: 'center',
    color:
      $isTipLocked || $isDisabled
        ? $isSelected
          ? primitive.$mayoEggWhite
          : primitive.$blackOpacity50
        : $isSelected
        ? primitive.$mayoEggWhite
        : primitive.$bbqBrown,
    fontSize: 'md',
    lineHeight: 18,
    ...props,
  });

const TipErrorMsg = ({ children, style, ...props }: BoxProps) => (
  <Box {...props} style={[styles.tipErrorContainer, style]}>
    <Typography style={styles.tipErrorText}>{children}</Typography>
  </Box>
);

const MAX_TIP_LENGTH = 6; // Dont allow number longer than $99.99

const DEFAULT_TIP_AMOUNT = '0';

export const TipSelector = ({
  isLoading,
  isDisabled: isExternallyDisabled,
}: BaseCheckoutWidgetProps) => {
  const { locale } = useLocale();
  const {
    isDisabled: isInternallyDisabled,
    tipSelection,
    isTipLocked,
    showTipPercentage,
    subTotalCents,
    additionalDiscountAmount,
    tipAmount: currentTipAmount,
    setTipSelection,
    updateTipAmount,
  } = useTip();
  const { isCheckoutLoading } = useCheckoutLoadingState();

  const isDisabled = isExternallyDisabled && isInternallyDisabled;

  const onTipPercentChange = useCallback(
    (tipPercent: string) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount: additionalDiscountAmount,
        newTipOption: 'percent',
        newTipAmount: parseFloat(tipPercent),
      });
    },
    [additionalDiscountAmount, subTotalCents, updateTipAmount]
  );

  const onTipAmountChange = useCallback(
    (newTipAmount: string | undefined) => {
      updateTipAmount({
        subTotalCents,
        otherDiscountAmount: additionalDiscountAmount,
        newTipOption: 'dollar',
        newTipAmount:
          newTipAmount === '' || typeof newTipAmount === 'undefined'
            ? undefined
            : parseFloat(newTipAmount),
      });
    },
    [additionalDiscountAmount, subTotalCents, updateTipAmount]
  );

  const { validate } = useCheckoutValidation();
  const checkoutErrors = validate();
  const errors = checkoutErrors.filter(error => error.placement === 'tipAmount');

  const tip = showTipPercentage ? tipSelection.percentAmount : tipSelection.dollarAmount;
  const currencyPrefix = getCurrencyPrefix(locale);
  const [tipString, setTipString] = useState(`${currencyPrefix}${tipSelection.otherAmount ?? ''}`);

  useLayoutEffect(() => {
    if (
      tipSelection.isOtherSelected &&
      tipSelection.otherAmount !== undefined &&
      tipString === currencyPrefix
    ) {
      setTipString(`${currencyPrefix}${tipSelection.otherAmount}`);
    }
  }, [currencyPrefix, tipSelection.isOtherSelected, tipSelection.otherAmount, tipString]);

  const updateOtherTipAmount = (otherAmount: string | undefined) => {
    setTipSelection({
      ...tipSelection,
      isOtherSelected: true,
      otherAmount: otherAmount === '' ? undefined : Number(otherAmount),
    });
    onTipAmountChange(otherAmount);
  };

  const handleTipAmountChange = (tip: string) => {
    const parsedTip = tip.replace(/[^0-9.]/g, '');
    setTipString(`${getCurrencyPrefix(locale)}${parsedTip}`);

    // Example: '1.2'
    const tipAmountAsString = parsedTip
      .split('')
      .filter(i => i !== getCurrencyPrefix(locale))
      .join('');

    updateOtherTipAmount(tipAmountAsString);
  };

  const onTipChange = useCallback(
    (amount: string) => {
      if (showTipPercentage) {
        onTipPercentChange(amount);
      } else {
        onTipAmountChange(amount);
      }
    },
    [showTipPercentage, onTipAmountChange, onTipPercentChange]
  );

  const buildRadioProps = (tipAmount: string | number) => {
    const numAmount = tipAmount === 'other' ? 0 : tipAmount;
    return {
      $isSelected: tipSelection.isOtherSelected ? tipAmount === 'other' : tip === tipAmount,
      onPress: () => {
        const isOtherSelected = tipAmount === 'other';

        let selection = {
          ...tipSelection,
          isOtherSelected,
          dollarAmount: tipAmount as number,
        };

        if (isDisabled || isTipLocked) {
          return;
        }

        if (tipAmount === 'other') {
          // Reset to default when changing to "Other"
          onTipAmountChange(DEFAULT_TIP_AMOUNT);
          selection = {
            ...tipSelection,
            isOtherSelected,
            otherAmount: undefined,
          };
        }
        if (showTipPercentage) {
          selection = {
            ...tipSelection,
            isOtherSelected,
            percentAmount: tipAmount as number,
          };
        }

        setTipSelection(selection);
        setTipString('');
        if (tipAmount !== 'other') {
          onTipChange(numAmount.toString());
        }
      },
    };
  };

  // Show the error message above the Tip select btns when the tip is > than the subtotal
  // If the user has a invalid tip, keep the error message when the user clicks on the 'other' button until the user start to type in the input
  const showTipSelectBtnError =
    !isLoading && (!tipSelection.isOtherSelected || !tipString) && currentTipAmount > subTotalCents;

  const tipOtherInputRef = useRef<RNTextInput>(null);
  const { isOtherSelected } = tipSelection;
  const previousTipOtherSelected = useRef(isOtherSelected);

  // focus tip input only when "Other" option is first selected
  // prevents keyboard from opening on mobile devices when cart renders Other by default
  useEffect(() => {
    if (!previousTipOtherSelected.current && isOtherSelected && tipOtherInputRef.current) {
      tipOtherInputRef.current.focus();
    }
  }, [isOtherSelected]);

  const tipData = showTipPercentage ? TipAmounts.PERCENT_AMOUNTS : TipAmounts.DOLLAR_AMOUNTS;

  return (
    <Box style={styles.container}>
      <Typography variant="headerThree">Delivery Tip</Typography>
      <TipList
        borderColor={isTipLocked ? primitive.$disabledTan : undefined}
        showsHorizontalScrollIndicator={false}
        horizontal
        accessibilityLabel="Select Tip"
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {tipData.map((amount, index) => {
          return (
            <PressableTip
              key={index}
              flex={1}
              $isTipLocked={isTipLocked}
              $isDisabled={isDisabled || isCheckoutLoading}
              accessibilityLabel={`${amount} tip`}
              {...buildRadioProps(amount)}
              $isFirst={index === 0}
            >
              {showTipPercentage ? (
                <TipText
                  $isDisabled={isDisabled}
                  $isTipLocked={isDisabled}
                  $isSelected={buildRadioProps(amount).$isSelected}
                >{`${amount}%`}</TipText>
              ) : (
                <Currency
                  amount={amount * 100}
                  isLoading={isLoading}
                  textProps={{
                    textAlign: 'center',
                    color: buildRadioProps(amount).$isSelected
                      ? primitive.$white
                      : isTipLocked || isDisabled
                      ? primitive.$blackOpacity50
                      : primitive.$greyOne,
                    fontSize: 'md',
                    lineHeight: 18,
                    style: { margin: 0 },
                  }}
                />
              )}
            </PressableTip>
          );
        })}
        <PressableTip
          flex={1}
          accessibilityLabel="Other tip"
          {...buildRadioProps('other')}
          $isLast
          $isTipLocked={isTipLocked}
          $isDisabled={isDisabled}
        >
          <TipText
            $isDisabled={isDisabled}
            $isSelected={tipSelection.isOtherSelected}
            $isTipLocked={isTipLocked}
          >
            {t('other')}
          </TipText>
        </PressableTip>
      </TipList>

      {showTipSelectBtnError && (
        <TipErrorMsg accessibilityRole="alert" testID="showTipSelectBtnError">
          {t('tipAmountMaxError')}
        </TipErrorMsg>
      )}
      {isOtherSelected && (
        <>
          <Box style={styles.addTipAmountContainer}>
            <Typography fontSize="md" bold style={styles.addTipAmountLabel}>
              Add Tip Amount
            </Typography>
          </Box>
          <TextInput
            type="text"
            value={tipString}
            errorMessage={!isCheckoutLoading ? errors[0]?.message ?? undefined : undefined}
            logUserErrorSeverity={LogUserErrorSeverity.Recoverable}
            onChangeText={handleTipAmountChange}
            maxLength={MAX_TIP_LENGTH}
            ref={tipOtherInputRef}
            keyboardType="numeric"
            returnKeyType="done"
            isDisabled={isDisabled || isTipLocked || isCheckoutLoading}
            placeholder="$0.00"
            placeholderTextColor={primitive.$blackOpacity20}
            noMargin
          />
        </>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  addTipAmountContainer: {
    alignSelf: 'flex-start',
    marginTop: spacing.$1,
  },
  addTipAmountLabel: {
    lineHeight: 14,
    marginBottom: spacing.$2,
  },
  container: {
    backgroundColor: primitive.$white,
    borderRadius: 10,
    paddingBottom: spacing.$3,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$5,
  },
  tipErrorContainer: {
    marginTop: spacing.$0_5,
  },
  tipErrorText: {
    color: getDesignToken('color-error'),
    fontSize: primitive.fontSizes['2xs'],
  },
});
